/**
 * 屏 1 — 关卡列表（V2 改造版）
 *
 * V2 将 Project 概念升级为 Level。屏 1 改为 3 tab 关卡列表：
 *   - [我的关卡]：从 project-store 获取已有项目（含 levelId 的项目）
 *   - [内置关卡]：从 LevelStore.list() 获取 _builtin 目录下 Level
 *   - [教学样例]：同内置关卡，按 difficulty=tutorial 筛选
 *
 * 每张关卡片显示：缩略图、关卡名、地图尺寸、难度星标、AI 名称、
 *   [进入编辑] + [Edit Map] 两个按钮
 *
 * Ardot v2.0 战术美学：
 *   - 卡片背景 #0D1117, border 2px #2A2A3A
 *   - hover/选中时 border → --tactical-cyan
 *   - 难度星标用 --valorant-red 填充
 *   - 4px 色条区分难度: tutorial=灰, easy=绿, medium=黄, hard=红
 *   - Tab 切换按钮用 Mono 字体 + [bracket] 标注
 */

import { listProjects, getProject, createFromLevel, listProjectsByLevelId, deleteProject, DEFAULT_BOT_CODE, STRATEGY_TEMPLATES, type Project } from "../storage/project-store";
import { createLevelStore, type LevelMeta } from "../storage/level-store";
import { pathFor } from "../router/screen-router";
// V5: 教学进度
import { loadProgress } from "../services/progress-store";
// V5 P1: 战前简报浮层
import { openBriefingModal, type BriefingData } from "../ui/briefing-modal";
import { svgLoader } from "../utils/svg-loader";
import { warmupPyodide } from "../engine/pyodide-pool";
import { TOKEN_MAP, tokenColor } from "../utils/color-tokens";
import { t } from "../i18n";
import { loadRules } from "../engine/rules-loader";

// ============================================================
// 类型
// ============================================================

type TabKey = "my-levels" | "arena" | "builtin" | "tutorial";

/** 卡片数据 — 统一 "我的关卡" 和 "内置关卡" 的渲染 */
interface LevelCardData {
  /** 卡片类型：my（已有项目）/ builtin（LevelStore 中的关卡，可能无项目）/ tutorial（同 builtin） */
  kind: "my" | "builtin" | "tutorial";
  /** LevelMeta 数据（我的关卡从 project.levelId 反查，可能为空） */
  meta: LevelMeta | null;
  /** 已有的 Project（我的关卡一定有，内置关卡可能已有项目） */
  project: Project | null;
}

// ============================================================
// 工具函数
// ============================================================

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

/** 难度 → 色条颜色（4px 左 border）FR-029: 改用 color-tokens */
function difficultyBorderColor(difficulty: string): string {
  switch (difficulty) {
    case "tutorial": return TOKEN_MAP.textMuted();     // 灰
    case "easy":     return TOKEN_MAP.green();         // 绿
    case "medium":   return TOKEN_MAP.amber();         // 黄
    case "hard":     return TOKEN_MAP.red();           // 红
    default:         return tokenColor("--bg-elevated", "#2A2A3A");
  }
}

/** 难度 → ★ 符号（1-5） */
function difficultyStars(stars: number): string {
  const filled = "★".repeat(Math.min(stars, 5));
  const empty = "☆".repeat(Math.max(0, 5 - stars));
  return `<span class="stars-filled">${filled}</span><span class="stars-empty">${empty}</span>`;
}

/** V5 关卡→阶段映射（与 progress-store.ts 的 PHASE_LEVELS 保持同步） */
const V5_PHASE_LEVELS: Record<number, string[]> = {
  0: ["v5_0-1_first_contact", "v5_0-2_find_enemy", "v5_0-3_moving_target"],
  1: ["v5_1-1_direction", "v5_1-2_cover_aware", "v5_1-3_cooldown"],
  2: ["v5_2-1_condition_basic", "v5_2-2_hp_gambit", "v5_2-3_multi_condition"],
  3: ["v5_3-1_scout_engage", "v5_3-2_state_switch", "v5_3-3_flank"],
  4: ["v5_4-1_full_plan", "v5_4-2_final_exercise"],
};

function getV5PhaseLevels(levelId: string): string[] | null {
  for (let p = 0; p <= 4; p++) {
    if (V5_PHASE_LEVELS[p]?.includes(levelId)) return V5_PHASE_LEVELS[p];
  }
  return null;
}

/** V5: 阶段→身份头衔映射 */
function getRankNameForPhase(phase: number): string {
  const RANKS: Record<number, string> = {
    0: "训练生",
    1: "战术新星",
    2: "对抗专家",
    3: "推演架构师",
    4: "推演架构师",
  };
  return RANKS[phase] ?? "推演官";
}

/** 进入关卡编辑器（创建或复用 project） */
async function enterLevel(
  levelId: string,
  onNavigate: (path: string) => void,
  allLevels: LevelMeta[],
  levelStore: ReturnType<typeof createLevelStore>,
  kind?: LevelCardData["kind"],
  projectId?: string,
): Promise<void> {
  if (kind === "my" && projectId) {
    onNavigate(pathFor("editor", projectId));
    return;
  }

  const existingProjects = listProjectsByLevelId(levelId);
  if (existingProjects.length > 0) {
    onNavigate(pathFor("editor", existingProjects[0].id));
    return;
  }

  if (isMultiTankLevel(levelId)) {
    openTemplateSelector(
      document.getElementById("app")!,
      onNavigate,
      levelId,
      allLevels,
      levelStore,
    );
    return;
  }

  let projId: string;
  try {
    const level = await levelStore.load(levelId);
    const proj = createFromLevel(levelId, level.name, level.playerTemplate || DEFAULT_BOT_CODE);
    projId = proj.id;
  } catch (err) {
    console.error(`[screen-1] 从关卡创建项目失败: ${levelId}`, err);
    const meta = allLevels.find((l) => l.levelId === levelId);
    const proj = createFromLevel(levelId, meta?.name ?? levelId, DEFAULT_BOT_CODE);
    projId = proj.id;
  }
  onNavigate(pathFor("editor", projId));
}

/** difficulty 字段 → 星级数（优先读 manifest 的 difficultyStars） */
function difficultyToStars(meta: LevelMeta): number {
  // A4: 优先从 manifest 映射过来的 difficultyStars 字段读取
  if (meta.difficultyStars !== undefined && meta.difficultyStars > 0) {
    return meta.difficultyStars;
  }
  // fallback: 基于 difficulty 字符串映射
  switch (meta.difficulty) {
    case "tutorial": return 1;
    case "easy":     return 2;
    case "medium":   return 3;
    case "hard":     return 4;
    default:         return 2;
  }
}

// ============================================================
// 主渲染函数
// ============================================================

export async function renderScreen1Projects(root: HTMLElement, onNavigate: (path: string) => void) {
  // V2: 屏 1 现在是异步的（需要从 LevelStore.list() 获取关卡数据）
  let activeTab: TabKey = "my-levels";

  // 加载数据
  const levelStore = createLevelStore();
  const allLevels = await levelStore.list().catch((e) => {
    console.warn("[screen-1] LevelStore.list() 失败:", e);
    return [] as LevelMeta[];
  });

  // 内置关卡（非 tutorial，且非 V5 教学关卡）
  const builtinLevels = allLevels.filter((l) => l.isBuiltin && l.difficulty !== "tutorial" && !l.levelId.startsWith("v5_"));
  // 教学样例（tutorial + 所有 V5 教学关卡）
  const tutorialLevels = allLevels.filter((l) => l.isBuiltin && (l.difficulty === "tutorial" || l.levelId.startsWith("v5_")));

  // 我的关卡：从 project-store 获取有 levelId 的项目（仅 user 类型）
  const userProjects = listProjects({ type: "user" });
  const myLevelProjects = userProjects.filter((p) => p.levelId);

  // Arena 战绩：从 project-store 获取 type="arena" 的项目
  const arenaProjects = listProjects({ type: "arena" });
  const arenaLevelProjects = arenaProjects.filter((p) => p.levelId);

  // 构建"我的关卡"卡片数据（project 驱动，仅 user 类型）
  const myCards: LevelCardData[] = myLevelProjects.map((p) => {
    const meta = allLevels.find((l) => l.levelId === p.levelId) ?? null;
    return { kind: "my", meta, project: p };
  });

  // 构建"Arena 战绩"卡片数据（project 驱动，arena 类型，kind="my" 因已有 project）
  const arenaCards: LevelCardData[] = arenaLevelProjects.map((p) => {
    const meta = allLevels.find((l) => l.levelId === p.levelId) ?? null;
    return { kind: "my", meta, project: p };
  });

  // 构建"内置关卡"卡片数据（LevelMeta 驱动）
  const builtinCards: LevelCardData[] = builtinLevels.map((meta) => {
    const existingProjects = listProjectsByLevelId(meta.levelId);
    const project = existingProjects.length > 0 ? existingProjects[0] : null;
    return { kind: "builtin", meta, project };
  });

  // 构建"教学样例"卡片数据（含进度状态）
  const v5Progress = loadProgress();
  const tutorialCards: LevelCardData[] = tutorialLevels.map((meta) => {
    const existingProjects = listProjectsByLevelId(meta.levelId);
    const project = existingProjects.length > 0 ? existingProjects[0] : null;
    return { kind: "tutorial", meta, project };
  });
  // V5: 教学关卡解锁/完成状态映射
  const tutorialStatusMap = new Map<string, "locked" | "unlocked" | "completed">();
  for (const card of tutorialCards) {
    const lid = card.meta?.levelId;
    if (!lid) continue;
    if (v5Progress.completedLevels.includes(lid)) {
      tutorialStatusMap.set(lid, "completed");
    } else if (v5Progress.currentPhase >= 0) {
      // 简化的解锁逻辑：同阶段前置关卡完成即解锁
      const phaseLevels = getV5PhaseLevels(lid);
      if (phaseLevels) {
        const idx = phaseLevels.indexOf(lid);
        const allPrevDone = idx <= 0 || phaseLevels.slice(0, idx).every((pl) => v5Progress.completedLevels.includes(pl));
        tutorialStatusMap.set(lid, allPrevDone ? "unlocked" : "locked");
      } else {
        tutorialStatusMap.set(lid, "unlocked");
      }
    }
  }

  const renderContent = () => {
    const isMyTab = activeTab === "my-levels";
    const isArenaTab = activeTab === "arena";
    const isBuiltinTab = activeTab === "builtin";
    const isTutorialTab = activeTab === "tutorial";

    const cards: LevelCardData[] = isMyTab ? myCards : isArenaTab ? arenaCards : isBuiltinTab ? builtinCards : tutorialCards;
    const tabLabel = isMyTab ? t("project.my_levels") : isArenaTab ? t("project.arena_records") : isBuiltinTab ? t("project.builtin_levels") : t("project.tutorial_levels");
    const cardCount = cards.length;

    root.innerHTML = `
    <div class="screen screen-1">
      <header class="screen-header">
        <h1 class="logo">BATTLE <span class="logo-accent">CODE</span><span class="logo-sub">${t("project.logo_sub")}</span></h1>
        <div class="screen-header-actions">
          <button class="btn btn-ghost" id="btn-home">◈ ${t("project.home_btn")}</button>
          <button class="btn btn-ghost" id="btn-forge">铸剑阁</button>
          <button class="btn btn-ghost btn-climb" id="btn-climb">${t("project.sharpen_btn")}</button>
          <button class="btn btn-ghost" id="btn-arena">${t("project.arena")}</button>
          <button class="btn btn-ghost" id="btn-settings">${t("project.settings_btn")}</button>
        </div>
      </header>

      <main class="screen-main screen1-main-v2">
        <h2 class="screen-title">${tabLabel}</h2>

        <!-- 4 tab 切换 -->
        <div class="screen1-tabs" role="tablist" aria-label="关卡来源切换">
          <button class="screen1-tab ${isMyTab ? "is-active" : ""}" role="tab"
            aria-selected="${isMyTab}" data-tab="my-levels" id="tab-my-levels">
            <span class="tab-bracket">[</span>${t("project.my_levels")}<span class="tab-bracket">]</span>
            <span class="screen1-tab-count">${myCards.length}</span>
          </button>
          <button class="screen1-tab ${isArenaTab ? "is-active" : ""}" role="tab"
            aria-selected="${isArenaTab}" data-tab="arena" id="tab-arena">
            <span class="tab-bracket">[</span>${t("project.arena_records")}<span class="tab-bracket">]</span>
            <span class="screen1-tab-count">${arenaCards.length}</span>
          </button>
          <button class="screen1-tab ${isBuiltinTab ? "is-active" : ""}" role="tab"
            aria-selected="${isBuiltinTab}" data-tab="builtin" id="tab-builtin">
            <span class="tab-bracket">[</span>${t("project.builtin_levels")}<span class="tab-bracket">]</span>
            <span class="screen1-tab-count">${builtinCards.length}</span>
          </button>
          <button class="screen1-tab ${isTutorialTab ? "is-active" : ""}" role="tab"
            aria-selected="${isTutorialTab}" data-tab="tutorial" id="tab-tutorial">
            <span class="tab-bracket">[</span>${t("project.tutorial_levels")}<span class="tab-bracket">]</span>
            <span class="screen1-tab-count">${tutorialCards.length}</span>
          </button>
        </div>

        <!-- 卡片网格 -->
        ${cardCount === 0 && isMyTab
          ? `<div class="empty-state">
              <img class="empty-state-img" src="/images/ui/empty-project.png" alt="" aria-hidden="true" loading="lazy">
              <p>${t("project.no_levels")}</p>
              <button class="btn btn-primary" id="btn-go-builtin">${t("project.go_builtin")}</button>
            </div>`
          : cardCount === 0 && isArenaTab
            ? `<div class="empty-state">
              <img class="empty-state-img" src="/images/ui/empty-project.png" alt="" aria-hidden="true" loading="lazy">
              <p>${t("project.no_arena_records")}</p>
              <button class="btn btn-primary" id="btn-go-arena">${t("project.go_arena")}</button>
            </div>`
          : cardCount === 0
            ? `<div class="empty-state">
              <img class="empty-state-img" src="/images/ui/empty-project.png" alt="" aria-hidden="true" loading="lazy">
              <p>${t("project.no_available")}</p>
            </div>`
            : `<div class="level-card-grid">
                ${cards.map((c) => renderLevelCard(c, tutorialStatusMap)).join("")}
              </div>`
        }
      </main>

      <footer class="screen-footer">
        ${isMyTab
          ? `<button class="btn btn-primary btn-lg" id="btn-from-level">
               <img class="icon-img" src="${svgLoader.getUrl("icon-plus") ?? ""}" alt="" aria-hidden="true" />${t("project.from_level")}
             </button>`
          : isArenaTab
            ? `<div class="screen-footer-hint" title="${t("project.arena_hint")}">
                 <img class="icon-img-sm" src="${svgLoader.getUrl("icon-tip") ?? ""}" alt="" aria-hidden="true" />
                 ${t("project.arena_hint")}
               </div>`
            : `<div class="screen-footer-hint" title="${t("project.select_level_hint")}">
                 <img class="icon-img-sm" src="${svgLoader.getUrl("icon-tip") ?? ""}" alt="" aria-hidden="true" />
                 ${t("project.select_level_hint")}
               </div>`
        }
      </footer>

      <!-- 日志路径提示 -->
      <div class="log-path-hint" title="${t("project.console_log_hint")}">
        <img class="icon-img-sm" src="${svgLoader.getUrl("icon-terminal") ?? ""}" alt="" aria-hidden="true" />${t("project.console_log_hint")}
      </div>
    </div>
    `;

    // ★ innerHTML 后立刻画 mini-map canvas(在事件绑定前,避免空 canvas 闪一下)
    drawMiniMaps(root, cards);

    // ====== 事件绑定 ======
    root.querySelector("#btn-home")?.addEventListener("click", () => onNavigate(pathFor("armory-hall")));
    root.querySelector("#btn-forge")?.addEventListener("click", () => onNavigate(pathFor("forge")));
    root.querySelector("#btn-climb")?.addEventListener("click", () => onNavigate(pathFor("sharpen-transition")));
    root.querySelector("#btn-settings")?.addEventListener("click", () => onNavigate(pathFor("settings")));
    root.querySelector("#btn-arena")?.addEventListener("click", () => onNavigate(pathFor("arena")));
    warmupPyodide();

    // tab 切换
    root.querySelectorAll<HTMLElement>("[data-tab]").forEach((el) => {
      el.addEventListener("click", () => {
        const t = el.dataset.tab as TabKey;
        if (t && t !== activeTab) {
          activeTab = t;
          renderContent();
        }
      });
    });

    // 我的关卡空白状态 → 跳内置关卡 tab
    root.querySelector("#btn-go-builtin")?.addEventListener("click", () => {
      activeTab = "builtin";
      renderContent();
    });

    // Arena 空状态 → 跳 Arena 屏
    root.querySelector("#btn-go-arena")?.addEventListener("click", () => {
      onNavigate(pathFor("arena"));
    });

    // "从关卡开始"按钮（我的关卡 tab footer）
    root.querySelector("#btn-from-level")?.addEventListener("click", () => {
      openLevelSelectModal(root, onNavigate, allLevels);
    });

    // V5 P1: 教学关卡卡片点击 → 打开战前简报浮层
    root.querySelectorAll<HTMLElement>(".level-card").forEach((card) => {
      card.addEventListener("click", async (e) => {
        // 不拦截按钮自身的 click（[进入编辑]/[Edit Map]/删除）
        const target = e.target as HTMLElement;
        if (target.closest("button")) return;

        const enterBtn = card.querySelector<HTMLElement>("[data-enter-editor]");
        if (!enterBtn) return;
        const levelId = enterBtn.dataset.enterEditor;
        const kind = enterBtn.dataset.cardKind as LevelCardData["kind"];
        if (!levelId || kind !== "tutorial") return;

        // 加载 manifest 提取 v5 数据
        try {
          const level = await levelStore.load(levelId);
          const rawManifest = (level as any)._rawManifest;
          const v5 = rawManifest?.v5;
          if (!v5) return;

          // 加载 archetype 数据
          let archetype: BriefingData["archetype"] = undefined;
          try {
            const archResp = await fetch("/data/lore/archetypes.json");
            if (archResp.ok) {
              const archData = await archResp.json();
              const found = (archData.archetypes || []).find(
                (a: any) => a.id === v5.aiArchetype
              );
              if (found) archetype = found;
            }
          } catch { /* 忽略 archetype 加载错误 */ }

          const briefingData: BriefingData = {
            levelId,
            levelName: level.name || rawManifest?.metadata?.name || levelId,
            phaseName: v5.phaseName || `阶段 ${v5.phase ?? 0}`,
            briefing: v5.narrative?.briefing || "",
            objective: v5.teachingBoard?.objectiveText || v5.mission?.description || "",
            archetype,
            apiSurface: v5.apiSurface || [],
            hintDensity: v5.teachingBoard?.hintDensity ?? 0,
            hintText: v5.teachingBoard?.hintText || "",
            rankName: getRankNameForPhase(v5.phase ?? 0),
          };
          openBriefingModal(briefingData);

          // 监听进入推演事件
          const onEnter = (ev: Event) => {
            const detail = (ev as CustomEvent).detail;
            if (detail?.levelId === levelId) {
              document.removeEventListener("briefing-enter", onEnter);
              enterLevel(levelId, onNavigate, allLevels, levelStore);
            }
          };
          document.addEventListener("briefing-enter", onEnter);
        } catch (err) {
          console.warn("[screen-1] 战前简报加载失败:", err);
          // fallback: 直接进入
          enterLevel(levelId, onNavigate, allLevels, levelStore);
        }
      });
    });

    // [进入编辑] 按钮
    root.querySelectorAll<HTMLElement>("[data-enter-editor]").forEach((el) => {
      el.addEventListener("click", async (e) => {
        e.stopPropagation();
        const levelId = el.dataset.enterEditor!;
        const kind = el.dataset.cardKind as LevelCardData["kind"];
        await enterLevel(levelId, onNavigate, allLevels, levelStore, kind, el.dataset.projectId);
      });
    });

    // [Edit Map] 按钮（跳屏 7，Phase 4 实现）
    root.querySelectorAll<HTMLElement>("[data-edit-map]").forEach((el) => {
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        const levelId = el.dataset.editMap!;
        onNavigate(`#/editor-map/${levelId}`);
      });
    });

    // 我的关卡卡片 — 删除按钮
    root.querySelectorAll<HTMLElement>("[data-project-delete]").forEach((el) => {
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        const id = el.dataset.projectDelete!;
        openConfirmModal(root, id, () => {
          // 删除后重新渲染（数据刷新）
          renderScreen1Projects(root, onNavigate);
        });
      });
    });
  };

  renderContent();
}

// ============================================================
// 卡片渲染
// ============================================================

/** V3-005: 判断关卡是否为多坦克模式 */
function isMultiTankLevel(levelId: string | undefined): boolean {
  if (!levelId) return false;
  try {
    const rules = loadRules(levelId);
    return (rules.constraints?.max_units_per_team ?? 1) > 1;
  } catch {
    return false;
  }
}

function renderLevelCard(card: LevelCardData, tutorialStatusMap?: Map<string, "locked" | "unlocked" | "completed">): string {
  const meta = card.meta;
  const proj = card.project;
  const isMy = card.kind === "my";
  const levelId = meta?.levelId ?? proj?.levelId;
  const multiTank = isMultiTankLevel(levelId);
  // V5: 教学关卡状态
  const tutorialStatus = tutorialStatusMap?.get(levelId ?? "") ?? "unlocked";
  const isLocked = tutorialStatus === "locked";
  const isCompleted = tutorialStatus === "completed";

  // 关卡名：我的关卡用 project.name，否则用 Level.name
  const displayName = isMy && proj
    ? escapeHtml(proj.name)
    : escapeHtml(meta?.name ?? t("project.unknown_level"));

  // 描述
  const description = meta?.description ?? "";

  // AI 名称
  const aiName = meta?.aiName ?? t("project.unknown_ai");

  // 难度色条
  const borderColor = difficultyBorderColor(meta?.difficulty ?? "easy");

  // 星标
  const stars = meta ? difficultyToStars(meta) : 2;

  // 缩略图：优先用 meta.map 真实 tile 数据画 canvas mini-map;
  //   meta.map 缺失时(老项目/异常 manifest)兜底用通用 SVG
  const hasRealMap = !!(meta?.map && meta.map.tiles && meta.map.tiles.length > 0);
  const thumbnailSrc = meta?.thumbnailSrc
    ? meta.thumbnailSrc
    : svgLoader.getUrl("mini-map-battle-easy") ?? "";

  // projectId（我的关卡用于跳转和删除）
  const projId = proj?.id ?? "";
  const commitCount = proj?.commitCount ?? 0;

  // 战术分隔符格式：CREATED 2026/07/08
  const createdAtTactical = proj?.createdAt
    ? new Date(proj.createdAt).toISOString().slice(0, 10).replace(/-/g, "/")
    : "—";

  // V3-005 Phase 3: 多坦克关卡卡片冷色指挥蓝
  const tacticalAccent = multiTank ? "var(--tactical-command-blue, #4A90D9)" : borderColor;

  return `
    <div class="level-card ${isMy ? "level-card--my" : ""} ${multiTank ? "level-card--tactical" : ""} ${isLocked ? "level-card--locked" : ""} ${isCompleted ? "level-card--completed" : ""}"
         style="--card-accent: ${tacticalAccent}">
      <!-- ★ Ardot v2.0: 顶部 2px 难度色细线(对齐 spec §6.3 Agent Select Card 选中态规则) -->
      <div class="level-card-topline" style="background: ${tacticalAccent}"></div>

      <!-- 缩略图(Ardot v2.0: 160×160 正方形,地图比例真实) -->
      <div class="level-card-thumb">
        ${hasRealMap
          ? `<canvas class="level-card-thumb-canvas"
                    data-minimap-levelid="${meta!.levelId}"
                    width="${meta!.map!.width}"
                    height="${meta!.map!.height}"></canvas>`
          : `<img class="level-card-thumb-img" src="${thumbnailSrc}"
                 alt="${displayName} thumbnail" width="200" height="200" loading="lazy" />`
        }
        <!-- 战术 callout 标签: 缩略图右下角显示双方 spawn 标识 -->
        <div class="level-card-corner-mark">
          <span class="corner-mark__a">A</span>
          <span class="corner-mark__sep">·</span>
          <span class="corner-mark__b">B</span>
        </div>
      </div>

      <!-- 信息区(Ardot v2.0 重构) -->
      <div class="level-card-body">
        <!-- 标题 + 难度星 + 2px 红色战术分割线 -->
        <div class="level-card-title-row">
          <h3 class="level-card-name">${displayName.toUpperCase()}</h3>
        </div>
        <div class="level-card-title-divider" style="background: ${multiTank ? "var(--tactical-command-blue, #4A90D9)" : "var(--valorant-red, #FF4655)"}"></div>

        <!-- 战术标签条: 难度 + AI 全大写 -->
        <div class="level-card-meta">
          <span class="level-card-stars" title="难度: ${meta?.difficulty ?? "easy"}">
            ${difficultyStars(stars)}
          </span>
          <span class="level-card-ai">
            <span class="meta-label">${t("project.opponent_ai")}</span>
            <span class="meta-value">${escapeHtml(aiName).toUpperCase().replace(/ /g, "_")}</span>
          </span>
        </div>

        ${description ? `<p class="level-card-desc">${escapeHtml(description)}</p>` : ""}

        <!-- 我的关卡: COMMITS · CREATED 元数据(Mono 全大写战术条) -->
        ${isMy && proj
          ? `<div class="level-card-project-info">
              <span class="meta-item">
                <span class="meta-key">${t("project.commits")}</span>
                <span class="meta-val">${commitCount}</span>
              </span>
              <span class="meta-sep">·</span>
              <span class="meta-item">
                <span class="meta-key">${t("project.created")}</span>
                <span class="meta-val">${createdAtTactical}</span>
              </span>
            </div>`
          : ""}

        <!-- 操作按钮(Ardot v2.0: DEPLOY / MAP / DEL 战术动词全大写) -->
        <div class="level-card-actions">
          <button class="btn btn-primary btn-sm"
                  data-enter-editor="${meta?.levelId ?? ""}"
                  data-card-kind="${card.kind}"
                  data-project-id="${projId}"
                  ${isLocked ? "disabled" : ""}>
            ${isLocked
              ? `🔒 锁定`
              : isCompleted
              ? `✓ 已完成`
              : `<img class="icon-img-sm" src="${svgLoader.getUrl("icon-arrow-right") ?? ""}" alt="" aria-hidden="true" />${multiTank ? "战术设计" : "DEPLOY"}`
            }
          </button>
          <button class="btn btn-ghost btn-sm"
                  data-edit-map="${meta?.levelId ?? ""}">
            <img class="icon-img-sm" src="${svgLoader.getUrl("icon-chart") ?? ""}" alt="" aria-hidden="true" />MAP
          </button>
          ${isMy && projId
            ? `<button class="btn btn-danger-ghost btn-sm" data-project-delete="${projId}" title="删除项目">
                 <img class="icon-img-sm" src="${svgLoader.getUrl("icon-trash") ?? ""}" alt="" aria-hidden="true" />
               </button>`
            : ""}
        </div>
        <!-- V5 教学关卡状态标签 -->
        ${card.kind === "tutorial"
          ? `<div class="level-card-tutorial-badge tutorial-badge-${tutorialStatus}">
              ${isLocked ? "🔒 未解锁" : isCompleted ? "✓ 已完成" : "可挑战"}
            </div>`
          : ""}
      </div>
    </div>
  `;
}

// ============================================================
// Modal: 模板选择器（多坦克关卡专用）
// ============================================================

function openTemplateSelector(
  _root: HTMLElement,
  onNavigate: (path: string) => void,
  levelId: string,
  allLevels: LevelMeta[],
  levelStore: ReturnType<typeof createLevelStore>,
) {
  closeModal();

  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.id = "bc-modal-backdrop";

  // 筛选适合该关卡的模板
  const unitsPerTeam = (() => {
    try {
      const rules = loadRules(levelId);
      return rules.constraints?.max_units_per_team ?? 1;
    } catch { return 1; }
  })();
  const templates = STRATEGY_TEMPLATES.filter(
    (t) => t.suitableFor === "both" || t.suitableFor === (unitsPerTeam === 2 ? "2v2" : "3v3"),
  );

  backdrop.innerHTML = `
    <div class="modal modal-lg" role="dialog" aria-labelledby="bc-modal-title">
      <h3 class="modal-title" id="bc-modal-title">选择战术模板</h3>
      <p class="modal-body">为 ${unitsPerTeam}v${unitsPerTeam} 编队选择初始战术模板，进入编辑器后可自由修改。</p>

      <div class="modal-template-list">
        ${templates.map((t) => `
          <div class="modal-template-card" data-template-id="${t.id}">
            <div class="modal-template-name">${escapeHtml(t.name)}</div>
            <div class="modal-template-desc">${escapeHtml(t.description)}</div>
            <div class="modal-template-suit">${t.suitableFor === "both" ? "2v2/3v3" : t.suitableFor}</div>
          </div>
        `).join("")}
        <div class="modal-template-card modal-template-card--blank" data-template-id="blank">
          <div class="modal-template-name">从空白开始</div>
          <div class="modal-template-desc">空模板，自由设计你的编队战术</div>
        </div>
      </div>

      <div class="modal-actions">
        <button class="btn btn-ghost" id="bc-modal-cancel">${t("common.cancel")}</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);

  // 模板卡片点击
  backdrop.querySelectorAll<HTMLElement>(".modal-template-card").forEach((card) => {
    card.addEventListener("click", async () => {
      const templateId = card.dataset.templateId;
      closeModal();

      const meta = allLevels.find((l) => l.levelId === levelId);
      try {
        const level = await levelStore.load(levelId);
        const proj = createFromLevel(levelId, level.name, level.playerTemplate || DEFAULT_BOT_CODE);

        if (templateId && templateId !== "blank") {
          // 覆盖默认模板为选中模板
          const { generateStrategyTemplate } = await import("../storage/project-store");
          const N = (() => {
            try { return loadRules(levelId).constraints?.max_units_per_team ?? 2; }
            catch { return 2; }
          })();
          proj.strategy_doc = generateStrategyTemplate(N, templateId as "triangle" | "defense" | "pincer");
        }

        const { saveProject } = await import("../storage/project-store");
        saveProject(proj);
        onNavigate(pathFor("editor", proj.id));
      } catch (err) {
        console.error(`[screen-1] 模板选择创建失败: ${levelId}`, err);
        const proj = createFromLevel(levelId, meta?.name ?? levelId, DEFAULT_BOT_CODE);
        onNavigate(pathFor("editor", proj.id));
      }
    });
  });

  backdrop.querySelector("#bc-modal-cancel")?.addEventListener("click", closeModal);
  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) closeModal();
  });
  document.addEventListener("keydown", escHandler);
  function escHandler(e: KeyboardEvent) {
    if (e.key === "Escape") {
      closeModal();
      document.removeEventListener("keydown", escHandler);
    }
  }
}

// ============================================================
// Modal: 从关卡创建项目
// ============================================================

function openLevelSelectModal(_root: HTMLElement, onNavigate: (path: string) => void, allLevels: LevelMeta[]) {
  closeModal();

  const levelStore = createLevelStore();
  // 所有可用关卡（内置 + 用户自建）
  const selectableLevels = allLevels;

  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.id = "bc-modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal modal-lg" role="dialog" aria-labelledby="bc-modal-title">
      <h3 class="modal-title" id="bc-modal-title">${t("project.from_level")}</h3>
      <p class="modal-body">${t("project.from_level_desc")}</p>

      <div class="modal-level-list">
        ${selectableLevels.length === 0
          ? `<p class="empty-state">${t("project.no_levels_modal")}</p>`
          : selectableLevels.map((l) => {
              const borderColor = difficultyBorderColor(l.difficulty);
              const stars = difficultyToStars(l);
              return `
                <div class="modal-level-item" data-level-id="${l.levelId}"
                     style="--card-accent: ${borderColor}">
                  <div class="modal-level-accent" style="background: ${borderColor}"></div>
                  <div class="modal-level-info">
                    <strong>${escapeHtml(l.name)}</strong>
                    <span class="modal-level-meta">
                      ${l.mapSize}×${l.mapSize} · ${difficultyStars(stars)} · ${escapeHtml(l.aiName)}
                    </span>
                    <span class="modal-level-desc">${escapeHtml(l.description)}</span>
                  </div>
                </div>
              `;
            }).join("")}
      </div>

      <!-- 空白项目入口 -->
      <div class="modal-blank-entry">
        <button class="btn btn-ghost" id="bc-modal-blank">${t("project.or_blank")}</button>
      </div>

      <div class="modal-actions">
        <button class="btn btn-ghost" id="bc-modal-cancel">${t("common.cancel")}</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);

  // 选择关卡
  backdrop.querySelectorAll<HTMLElement>(".modal-level-item").forEach((el) => {
    el.addEventListener("click", async () => {
      const levelId = el.dataset.levelId!;
      closeModal();

      try {
        const level = await levelStore.load(levelId);
        const proj = createFromLevel(levelId, level.name, level.playerTemplate || DEFAULT_BOT_CODE);
        onNavigate(pathFor("editor", proj.id));
      } catch (err) {
        console.error(`[screen-1] 从关卡创建项目失败: ${levelId}`, err);
        // fallback
        const meta = allLevels.find((l) => l.levelId === levelId);
        const proj = createFromLevel(levelId, meta?.name ?? levelId, DEFAULT_BOT_CODE);
        onNavigate(pathFor("editor", proj.id));
      }
    });
  });

  // 空白项目
  backdrop.querySelector("#bc-modal-blank")?.addEventListener("click", () => {
    closeModal();
    // 动态 import saveProject 避免循环依赖
    import("../storage/project-store").then(({ saveProject, generateProjectId }) => {
      const now = Date.now();
      const proj: Project = {
        id: generateProjectId(),
        name: t("project.default_name"),
        code: DEFAULT_BOT_CODE,
        createdAt: now,
        updatedAt: now,
        commitCount: 0,
        type: "user",
      };
      saveProject(proj);
      onNavigate(pathFor("editor", proj.id));
    });
  });

  backdrop.querySelector("#bc-modal-cancel")?.addEventListener("click", closeModal);
  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) closeModal();
  });
  document.addEventListener("keydown", escHandler);
  function escHandler(e: KeyboardEvent) {
    if (e.key === "Escape") {
      closeModal();
      document.removeEventListener("keydown", escHandler);
    }
  }
}

// ============================================================
// Modal: 删除确认
// ============================================================

function openConfirmModal(_root: HTMLElement, projectId: string, onDeleted: () => void) {
  closeModal();

  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.id = "bc-modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal" role="dialog" aria-labelledby="bc-modal-title">
      <h3 class="modal-title" id="bc-modal-title">${t("project.delete_title")}</h3>
      <p class="modal-body">${t("project.delete_body")}</p>
      <div class="modal-actions">
        <button class="btn btn-ghost" id="bc-modal-cancel">${t("common.cancel")}</button>
        <button class="btn btn-danger" id="bc-modal-ok">${t("common.delete")}</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);

  backdrop.querySelector("#bc-modal-cancel")?.addEventListener("click", closeModal);
  backdrop.querySelector("#bc-modal-ok")?.addEventListener("click", async () => {
    const project = getProject(projectId);
    const levelId = project?.levelId;
    const stillReferenced = levelId
      ? listProjectsByLevelId(levelId).some((item) => item.id !== projectId)
      : true;
    deleteProject(projectId);
    if (levelId && !stillReferenced) {
      try {
        const { createLevelStore } = await import("../storage/level-store");
        const level = await createLevelStore().load(levelId);
        if (!level.isBuiltin) await createLevelStore().delete(levelId);
      } catch (error) {
        console.warn(`[project-list] 级联删除关卡失败: ${levelId}`, error);
      }
    }
    closeModal();
    onDeleted();
  });
  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) closeModal();
  });
  document.addEventListener("keydown", escHandler);
  function escHandler(e: KeyboardEvent) {
    if (e.key === "Escape") {
      closeModal();
      document.removeEventListener("keydown", escHandler);
    }
  }
}

function closeModal() {
  document.getElementById("bc-modal-backdrop")?.remove();
}

// ============================================================
// mini-map canvas 渲染 — 用 map.json 真实 tile 数据画缩略图
// ============================================================

/**
 * 遍历 root 下所有 canvas[data-minimap-levelid],用对应的 map 数据画 mini-map。
 *
 * 颜色映射(对齐 Ardot v2.0 战术美学 + tile 语义):
 *   empty(0)       → #0D1117  深背景(几乎不可见,作底)
 *   cover(1)       → #00E4FF  tactical-cyan(掩体,可见)
 *   obstacle(2)    → #4A5568  中灰(障碍,实墙)
 *   unreachable(3) → #1A1A2E  更深灰(不可达,边界)
 *   spawnA         → #4EC9B0  队A 绿
 *   spawnB         → #F25C54  队B 红
 *
 * canvas 原生 width/height = 地图尺寸(如 25),CSS 拉伸到容器 100%,
 * image-rendering: pixelated 保持锐角,符合 v2 战术美学。
 */
function drawMiniMaps(root: HTMLElement, cards: LevelCardData[]): void {
  // 用 levelId lookup meta.map,避免每个 canvas 都重新查 cards
  const mapByLevelId = new Map<string, NonNullable<LevelMeta["map"]>>();
  for (const c of cards) {
    if (c.meta?.map && c.meta.levelId) {
      mapByLevelId.set(c.meta.levelId, c.meta.map);
    }
  }

  // ★ 每个 tile 占 CELL_SIZE×CELL_SIZE 像素,canvas 原生尺寸 = 地图尺寸 × CELL_SIZE
  //   CELL_SIZE=8 → 25×25 地图 → 200×200 canvas(CSS 容器也是 200,1:1 无缩放)
  //                  15×15 地图 → 120×120 canvas(CSS 拉伸到 200,pixelated 保持锐角)
  //   必须放大:25×25 原生尺寸画 1px 网格线,CSS 缩放 8x 后变成 8px 粗线,完全看不见细节
  const CELL_SIZE = 8;

  const canvases = root.querySelectorAll<HTMLCanvasElement>("canvas[data-minimap-levelid]");
  canvases.forEach((canvas) => {
    const levelId = canvas.dataset.minimapLevelid;
    if (!levelId) return;
    const map = mapByLevelId.get(levelId);
    if (!map) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { width, height, tiles, spawnA, spawnB } = map;
    // ★ 设 canvas 原生尺寸 = 地图尺寸 × CELL_SIZE(覆盖 HTML attribute 的 width/height)
    const canvasW = width * CELL_SIZE;
    const canvasH = height * CELL_SIZE;
    canvas.width = canvasW;
    canvas.height = canvasH;

    const TILE_COLORS: Record<number, string> = {
      0: TOKEN_MAP.bgDeep(),              // empty
      1: TOKEN_MAP.cyan(),                // cover
      2: TOKEN_MAP.textSecondary(),       // obstacle
      3: tokenColor("--bg-void", "#1A1A2E"),  // unreachable
    };

    // 1. 画 tiles(每 tile 占 CELL_SIZE×CELL_SIZE 像素块)
    for (let y = 0; y < height; y++) {
      const row = tiles[y];
      if (!row) continue;
      for (let x = 0; x < width; x++) {
        const tile = row[x] ?? 0;
        ctx.fillStyle = TILE_COLORS[tile] ?? TILE_COLORS[0];
        ctx.fillRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
      }
    }

    // 2. 画网格线(让空旷地形也有"战术地图"结构感)
    //    每 5 格画一条 1px 中灰线(主网格),每 1 格画 0.5px 浅灰线(细网格)
    //    颜色用 Ardot 战术美学的灰阶: #1F1F2E(细) / #2A2A3A(主) / #3A3A4A(边框)
    ctx.lineWidth = 0.5;
    ctx.strokeStyle = TOKEN_MAP.bgDeep();  // 细网格(每格)
    ctx.beginPath();
    for (let i = 0; i <= width; i++) {
      ctx.moveTo(i * CELL_SIZE + 0.5, 0);
      ctx.lineTo(i * CELL_SIZE + 0.5, canvasH);
    }
    for (let i = 0; i <= height; i++) {
      ctx.moveTo(0, i * CELL_SIZE + 0.5);
      ctx.lineTo(canvasW, i * CELL_SIZE + 0.5);
    }
    ctx.stroke();

    // 主网格(每 5 格,1px，颜色用 bg-elevated)
    ctx.lineWidth = 1;
    ctx.strokeStyle = tokenColor("--bg-elevated", "#2A2A3A");
    ctx.beginPath();
    for (let i = 0; i <= width; i += 5) {
      ctx.moveTo(i * CELL_SIZE + 0.5, 0);
      ctx.lineTo(i * CELL_SIZE + 0.5, canvasH);
    }
    for (let i = 0; i <= height; i += 5) {
      ctx.moveTo(0, i * CELL_SIZE + 0.5);
      ctx.lineTo(canvasW, i * CELL_SIZE + 0.5);
    }
    ctx.stroke();

    // 3. 画 spawn(占 1 个 tile,但稍大 2px 边框让它在网格中更突出)
    //    spawn A(队A 绿)
    ctx.fillStyle = TOKEN_MAP.cyan();
    ctx.fillRect(spawnA[0] * CELL_SIZE + 1, spawnA[1] * CELL_SIZE + 1, CELL_SIZE - 2, CELL_SIZE - 2);
    //    spawn B(队B 红)
    ctx.fillStyle = TOKEN_MAP.red();
    ctx.fillRect(spawnB[0] * CELL_SIZE + 1, spawnB[1] * CELL_SIZE + 1, CELL_SIZE - 2, CELL_SIZE - 2);

    // 4. 边框(让 mini-map 整体有清晰边界)
    ctx.lineWidth = 1;
    ctx.strokeStyle = TOKEN_MAP.textSecondary();
    ctx.strokeRect(0.5, 0.5, canvasW - 1, canvasH - 1);
  });
}
